import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Pencil, ArrowUpDown, Download, Loader, Filter, User, DollarSign, Info, Users, Briefcase, Trash } from 'lucide-react';

import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const CombinedDashboard = () => {
    const navigate = useNavigate();
    const [brokers, setBrokers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loadingBrokers, setLoadingBrokers] = useState(true);
    const [loadingClients, setLoadingClients] = useState(true);
    const [brokerSearchTerm, setBrokerSearchTerm] = useState('');
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [brokerSortConfig, setBrokerSortConfig] = useState({ key: null, direction: 'ascending' });
    const [clientSortConfig, setClientSortConfig] = useState({ key: null, direction: 'ascending' });
    const [hoveredBrokerRow, setHoveredBrokerRow] = useState(null);
    const [hoveredClientRow, setHoveredClientRow] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const handleEdit = (data, type) => {
        setEditingUser({
            _id: data._id,
            name: data.name,
            userId: data.userId,
            idPercentage: data.idPercentage,
            commission: data.commission,
            type,
        });
        setShowModal(true);
    };

    useEffect(() => {
        // Fetch referredBy employee list for dropdown
        const fetchEmployees = async () => {
            try {
                const res = await fetch('https://api.happychd.com/api/employees');
                const data = await res.json();
                setEmployeeOptions(data);
            } catch (error) {
                console.error('Failed to fetch employees', error);
            }
        };
        fetchEmployees();
    }, []);

    const handleSaveEdit = async () => {
        const { _id, name, userId, idPercentage, commission, referredBy, type } = editingUser;
        console.log(editingUser)
        const endpoint = type === 'broker' ? 'broker' : 'user';

        try {
            const res = await fetch(`https://api.happychd.com/api/${endpoint}/${_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, userId, idPercentage, referredBy, commission }),
            });

            if (res.ok) {
                alert("✅ Updated successfully");
                setEditingUser(null);
                setShowModal(false);
                window.location.reload(); // or refetch state
            } else {
                console.error("Failed to update");
            }
        } catch (err) {
            console.error(err.message);
        }
    };

    const handleDelete = async (item, type) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete this ${type}?`);
        if (!confirmDelete) return;

        const endpoint = type === 'broker' ? 'broker' : 'user';

        try {
            const res = await fetch(`https://api.happychd.com/api/${endpoint}/${item._id}`, {
                method: 'DELETE',
            });

            if (res.ok) {
                alert(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
                window.location.reload(); // or refetch the state if you want smoother UX
            } else {
                const data = await res.json();
                alert(`❌ Failed to delete: ${data.message || 'Unknown error'}`);
            }
        } catch (err) {
            console.error(err.message);
            alert('❌ Error deleting. Please try again.');
        }
    };


    // Function to safely convert values to numbers
    const toNumber = (val) => Number(val || 0);

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch brokers
                const brokerRes = await fetch('https://api.happychd.com/api/broker');
                const brokerData = await brokerRes.json();
                setBrokers(brokerData || []);
                setLoadingBrokers(false);

                // Fetch clients
                const clientRes = await fetch('https://api.happychd.com/api/user');
                const clientData = await clientRes.json();
                setClients(clientData || []);
                setLoadingClients(false);
            } catch (err) {
                console.error('Error fetching data:', err.message);
                setLoadingBrokers(false);
                setLoadingClients(false);
            }
        };

        fetchData();
    }, []);

    // Calculate all derived values for a user or broker
    const computeData = (item) => {
        const {
            name,
            userId,
            commission = 0,
            idPercentage = 0,
            transactions = [],
            paymentStatus = [],
            balanceHistory = [],
        } = item;

        const totalDeposit = transactions
            .filter((t) => t.type === 'deposit')
            .reduce((sum, t) => sum + toNumber(t.amount), 0);

        const totalWithdraw = transactions
            .filter((t) => t.type === 'withdraw')
            .reduce((sum, t) => sum + toNumber(t.amount), 0);

        const totalPaymentStatus = paymentStatus.reduce((sum, p) => sum + toNumber(p.amount), 0);
        const totalBalance = balanceHistory.reduce((sum, b) => sum + toNumber(b.amount), 0);

  const totalDepositToId = (item.depositToId || [])
    .reduce((sum, d) => sum + toNumber(d.amount), 0);

        const rawIdSettled = (totalWithdraw + totalBalance - totalDeposit) * (idPercentage / 100);
        const cmsnSettled = rawIdSettled < 0 ? Math.abs(rawIdSettled * (commission / 100)) : 0;

        const idSettled = (rawIdSettled + cmsnSettled);
        const netSettlement = totalPaymentStatus + idSettled;

        return {
            name,
            userId,
            idPercentage,
            commission,
            totalPaymentStatus,
            totalDeposit,
            totalWithdraw,
            totalBalance,
   totalDepositToId,
            rawIdSettled,
            cmsnSettled,
            idSettled,
            netSettlement
        };
    };

    // Function to handle sorting for brokers
    const requestBrokerSort = (key) => {
        let direction = 'ascending';
        if (brokerSortConfig.key === key && brokerSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setBrokerSortConfig({ key, direction });
    };

    // Function to handle sorting for clients
    const requestClientSort = (key) => {
        let direction = 'ascending';
        if (clientSortConfig.key === key && clientSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setClientSortConfig({ key, direction });
    };

    // Sort and filter brokers
    const sortedBrokers = React.useMemo(() => {
        let sortableBrokers = [...brokers];

        // Filter by search term
        if (brokerSearchTerm) {
            sortableBrokers = sortableBrokers.filter(broker =>
                (broker.name && broker.name.toLowerCase().includes(brokerSearchTerm.toLowerCase())) ||
                (broker.userId && broker.userId.toString().includes(brokerSearchTerm.toLowerCase())) ||
                (broker.referredBy?.userId && broker.referredBy?.userId.toString().includes(brokerSearchTerm.toLowerCase()))
            );
        }

        // Sort by selected column
        if (brokerSortConfig.key) {
            sortableBrokers.sort((a, b) => {
                const dataA = computeData(a);
                const dataB = computeData(b);

                let valueA = brokerSortConfig.key.includes('.')
                    ? dataA[brokerSortConfig.key.split('.')[1]]
                    : dataA[brokerSortConfig.key];

                let valueB = brokerSortConfig.key.includes('.')
                    ? dataB[brokerSortConfig.key.split('.')[1]]
                    : dataB[brokerSortConfig.key];

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    valueA = valueA.toLowerCase();
                    valueB = valueB.toLowerCase();
                }

                if (valueA < valueB) {
                    return brokerSortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valueA > valueB) {
                    return brokerSortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableBrokers;
    }, [brokers, brokerSortConfig, brokerSearchTerm]);

    // Sort and filter clients
    const sortedClients = React.useMemo(() => {
        let sortableClients = [...clients];

        // Filter by search term
        if (clientSearchTerm) {
            sortableClients = sortableClients.filter(client =>
                (client.name && client.name.toLowerCase().includes(clientSearchTerm.toLowerCase())) ||
                (client.userId && client.userId.toString().includes(clientSearchTerm.toLowerCase())) ||
                (client.referredBy?.userId && client.referredBy?.userId.toString().includes(clientSearchTerm.toLowerCase()))
            );
        }

        // Sort by selected column
        if (clientSortConfig.key) {
            sortableClients.sort((a, b) => {
                const dataA = computeData(a);
                const dataB = computeData(b);

                let valueA = clientSortConfig.key.includes('.')
                    ? dataA[clientSortConfig.key.split('.')[1]]
                    : dataA[clientSortConfig.key];

                let valueB = clientSortConfig.key.includes('.')
                    ? dataB[clientSortConfig.key.split('.')[1]]
                    : dataB[clientSortConfig.key];

                if (typeof valueA === 'string' && typeof valueB === 'string') {
                    valueA = valueA.toLowerCase();
                    valueB = valueB.toLowerCase();
                }

                if (valueA < valueB) {
                    return clientSortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (valueA > valueB) {
                    return clientSortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableClients;
    }, [clients, clientSortConfig, clientSearchTerm]);

    // Export broker data as CSV
    const exportBrokersToCSV = () => {
        const headers = [
            'Sr.No.',
            'Name',
            'Payment Status',
            'ID',
            'ID %',
            'Commission %',
            'Total Deposited To ID',
            'Total Deposit',
            'Total Withdraw',
            'Balance',
            'Raw ID Settled',
            'Cmsn Settled',
            'ID Settled',
            'Net Settlement'
        ];

        const rows = sortedBrokers.map((broker, idx) => {
            const data = computeData(broker);
            return [
                idx + 1,
                data.name || 'N/A',
                data.totalPaymentStatus.toFixed(2),
                data.userId,
                data.idPercentage,
                data.commission,
                data.totalDepositToId.toFixed(2),
                data.totalDeposit.toFixed(2),
                data.totalWithdraw.toFixed(2),
                data.totalBalance.toFixed(2),
                data.rawIdSettled.toFixed(2),
                data.cmsnSettled.toFixed(2),
                data.idSettled.toFixed(2),
                data.netSettlement.toFixed(2)
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'brokers_data.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export client data as CSV
    const exportClientsToCSV = () => {
        const headers = [
            'Sr.No.',
            'Name',
            'Payment Status',
            'ID',
            'ID %',
            'Commission %',
            'Total Deposit',
            'Total Withdraw',
            'Balance',
            'Raw ID Settled',
            'Cmsn Settled',
            'ID Settled',
            'Net Settlement'
        ];

        const rows = sortedClients.map((client, idx) => {
            const data = computeData(client);
            return [
                idx + 1,
                data.name || 'N/A',
                data.totalPaymentStatus.toFixed(2),
                data.userId,
                data.idPercentage,
                data.commission,
                data.totalDeposit.toFixed(2),
                data.totalWithdraw.toFixed(2),
                data.totalBalance.toFixed(2),
                data.rawIdSettled.toFixed(2),
                data.cmsnSettled.toFixed(2),
                data.idSettled.toFixed(2),
                data.netSettlement.toFixed(2)
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'clients_data.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Calculate combined totals for summary stats
    const calculateCombinedTotals = () => {
        const brokerNetSettlement = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + data.netSettlement;
        }, 0);

        const clientAdjustment = clients.reduce((sum, client) => {
            const data = computeData(client);
            // Reverse sign of client netSettlement
            const adjusted = data.netSettlement > 0 ? -Math.abs(data.netSettlement) : Math.abs(data.netSettlement);
            return sum + adjusted;
        }, 0);

        const totalNetSettlement = brokerNetSettlement + clientAdjustment;

        const totalDeposit = [...brokers, ...clients].reduce((sum, item) => sum + computeData(item).totalDeposit, 0);
        const totalWithdraw = [...brokers, ...clients].reduce((sum, item) => sum + computeData(item).totalWithdraw, 0);
        const totalBalance = [...brokers, ...clients].reduce((sum, item) => sum + computeData(item).totalBalance, 0);
        const totalCommission = [...brokers, ...clients].reduce((sum, item) => sum + Math.abs(computeData(item).cmsnSettled), 0);

        return {
            totalDeposit,
            totalWithdraw,
            totalBalance,
            totalCommission,
            netSettlement: totalNetSettlement
        };
    };

    // Calculate broker totals
    const calculateBrokerTotals = () => {
        const totalDeposit = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + data.totalDeposit;
        }, 0);

        const totalWithdraw = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + data.totalWithdraw;
        }, 0);

        const totalBalance = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + data.totalBalance;
        }, 0);

        const totalCommission = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + Math.abs(data.cmsnSettled);
        }, 0);

        const netSettlement = brokers.reduce((sum, broker) => {
            const data = computeData(broker);
            return sum + data.netSettlement;
        }, 0);

        return {
            totalDeposit,
            totalWithdraw,
            totalBalance,
            totalCommission,
            netSettlement
        };
    };

    // Calculate client totals
    const calculateClientTotals = () => {
        const totalDeposit = clients.reduce((sum, client) => {
            const data = computeData(client);
            return sum + data.totalDeposit;
        }, 0);

        const totalWithdraw = clients.reduce((sum, client) => {
            const data = computeData(client);
            return sum + data.totalWithdraw;
        }, 0);

        const totalBalance = clients.reduce((sum, client) => {
            const data = computeData(client);
            return sum + data.totalBalance;
        }, 0);

        const totalCommission = clients.reduce((sum, client) => {
            const data = computeData(client);
            return sum + Math.abs(data.cmsnSettled);
        }, 0);

        const netSettlement = clients.reduce((sum, client) => {
            const data = computeData(client);
            return sum + data.netSettlement;
        }, 0);

        return {
            totalDeposit,
            totalWithdraw,
            totalBalance,
            totalCommission,
            netSettlement
        };
    };

    // Format currency values
    const formatCurrency = (value) => {
        return `₹${value.toFixed(2)}`;
    };

    const groupByReferrer = (items) => {
        const groups = {};

        items.forEach((item) => {
            const refName = item.referredBy?.userId || 'Unassigned';
            if (!groups[refName]) {
                groups[refName] = [];
            }
            groups[refName].push(item);
        });

        return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
    };

    // Loading state
    if (loadingBrokers || loadingClients) {
        return (
            <div className="p-6 flex flex-col items-center justify-center h-64 text-gray-600">
                <Loader className="animate-spin h-8 w-8 mb-4 text-orange-500" />
                <p className="text-lg">Loading data...</p>
            </div>
        );
    }

    // Enhanced Modal Edit Component
    const EditableRow = () => {
        if (!showModal || !editingUser) return null;

        const isBroker = editingUser.type === 'broker';
        const baseUserTypePath = isBroker ? 'broker' : 'client'; // dynamic path based on type
        const userTypeLabel = isBroker ? 'Broker' : 'Client';
        const { _id, userId } = editingUser;

        const navigateTo = (type) => {
            let basePath = `/${baseUserTypePath}/`;
            switch (type) {
                case 'deposit':
                    basePath += `deposit/${_id}/${userId}`;
                    break;
                case 'withdraw':
                    basePath += `withdraw/${_id}/${userId}`;
                    break;
                case 'payment':
                    basePath += `payment-status/${_id}/${userId}`;
                    break;
                case 'transactions':
                    basePath += `transactions/${_id}/${userId}`;
                    break;
                case 'balance':
                    basePath += `balance-withdrawal/${_id}/${userId}`;
                    break;
                default:
                    return;
            }
            navigate(basePath);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-800">Edit {userTypeLabel}</h3>
                        <button
                            onClick={() => setShowModal(false)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                                type="text"
                                value={editingUser.name || ''}
                                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                            <input
                                type="text"
                                value={editingUser.userId || ''}
                                onChange={(e) => setEditingUser({ ...editingUser, userId: e.target.value })}
                                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="ID"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">ID Percentage</label>
                                <input
                                    type="number"
                                    value={editingUser.idPercentage || 0}
                                    onChange={(e) => setEditingUser({ ...editingUser, idPercentage: e.target.value })}
                                    className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="ID %"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Commission Percentage</label>
                                <input
                                    type="number"
                                    value={editingUser.commission || 0}
                                    onChange={(e) => setEditingUser({ ...editingUser, commission: e.target.value })}
                                    className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Commission %"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Guarantee</label>
                            <select
                                value={editingUser.referredBy || ''}
                                onChange={(e) => setEditingUser({ ...editingUser, referredBy: e.target.value })}
                                className="w-full border px-3 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Guarantee</option>
                                {employeeOptions.map((emp) => (
                                    <option key={emp._id} value={emp._id}>
                                        {emp.userId}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>



                    {/* Management Buttons Section */}
                    <div className="grid grid-cols-2 gap-3 mt-6">
                        <button
                            onClick={() => navigateTo('deposit')}
                            className="bg-green-600 hover:bg-green-700 text-white py-2 rounded"
                        >
                            💰 Deposit
                        </button>
                        <button
                            onClick={() => navigateTo('withdraw')}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded"
                        >
                            🏧 Withdraw
                        </button>
                        <button
                            onClick={() => navigateTo('payment')}
                            className="bg-purple-600 hover:bg-purple-700 text-white py-2 rounded"
                        >
                            📄 Payment
                        </button>
                        <button
                            onClick={() => navigateTo('transactions')}
                            className="bg-blue-500 hover:bg-blue-600 text-white py-2 rounded"
                        >
                            🔄 Transactions
                        </button>
                        <button
                            onClick={() => navigateTo('balance')}
                            className="bg-red-500 hover:bg-red-600 text-white py-2 rounded col-span-2"
                        >
                            💼 Balance Summary
                        </button>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={() => setShowModal(false)}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveEdit}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Table column definitions
    const columns = [
        { key: 'index', label: 'Sr.No.', sortable: true },
        { key: 'referredBy', label: 'Guarantee', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'userId', label: 'ID', sortable: true },
        { key: 'totalPaymentStatus', label: 'Payment Status', sortable: true },
        { key: 'idPercentage', label: 'ID %', sortable: true },
        { key: 'commission', label: 'Commission %', sortable: true },
        { key: 'totalDepositToId', label: 'Total Deposited To ID', sortable: true },
        { key: 'totalDeposit', label: 'Total Deposit', sortable: true },
        { key: 'totalWithdraw', label: 'Total Withdraw', sortable: true },
        { key: 'totalBalance', label: 'Balance Withdrawal', sortable: true },
        { key: 'rawIdSettled', label: 'Raw ID Settled', sortable: true },
        { key: 'cmsnSettled', label: 'Cmsn Settled', sortable: true },
        { key: 'idSettled', label: 'ID Settled', sortable: true },
        { key: 'netSettlement', label: 'Net Settlement', sortable: true },
        { key: 'action', label: 'Action', sortable: true },
    ];

    const clientColumns = columns.filter(col => col.key !== 'totalDepositToId');

    // Get combined totals for summary
    const combinedTotals = calculateCombinedTotals();
    const brokerTotals = calculateBrokerTotals();
    const clientTotals = calculateClientTotals();

    const updateAll = async () => {
        const result = await Swal.fire({
            title: '🛠️ Update Brokers and Clients?',
            text: 'This will update all brokers and clients who have a net balance of 0. This action is irreversible!',
            icon: 'info',
            background: '#f0f9ff',
            color: '#0f172a',
            showCancelButton: true,
            confirmButtonColor: '#ff5c00',
            cancelButtonColor: '#64748b',
            confirmButtonText: '✅ Yes, update them!',
            cancelButtonText: '❌ Cancel',
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch('https://api.happychd.com/api/broker/cleanup-zero-balance', {
                    method: 'PUT',
                });

                if (res.ok) {
                    Swal.fire({
                        title: '✅ Success!',
                        text: 'Updated all zero balance brokers successfully.',
                        icon: 'success',
                        confirmButtonColor: '#ff5c00',
                    });
                    window.location.reload();
                } else {
                    const errData = await res.json();
                    Swal.fire({
                        title: '❌ Failed!',
                        text: errData.message || 'Unknown error occurred.',
                        icon: 'error',
                        confirmButtonColor: '#ff5c00',
                    });
                }
            } catch (err) {
                console.error(err);
                Swal.fire({
                    title: '❌ Network Error!',
                    text: 'There was a network or server error while updating.',
                    icon: 'error',
                    confirmButtonColor: '#ff5c00',
                });
            }
        }
    };

    const deleteAll = async () => {
        const result = await Swal.fire({
            title: '⚠️ Delete Everything?',
            text: 'This will PERMANENTLY DELETE all Brokers and Clients! This cannot be undone.',
            icon: 'warning',
            background: '#fff1f2',
            color: '#991b1b',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: '🔥 Yes, delete all!',
            cancelButtonText: '❌ Cancel',
            customClass: {
                confirmButton: 'swal2-confirm btn-danger',
            }
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch('https://api.happychd.com/api/broker/deleteAll', {
                    method: 'DELETE',
                });

                if (res.ok) {
                    Swal.fire({
                        title: '✅ Deleted!',
                        text: 'All brokers and clients deleted successfully.',
                        icon: 'success',
                        confirmButtonColor: '#ff5c00',
                    });
                    window.location.reload();
                } else {
                    const errData = await res.json();
                    Swal.fire({
                        title: '❌ Failed!',
                        text: errData.message || 'Unknown error occurred.',
                        icon: 'error',
                        confirmButtonColor: '#ff5c00',
                    });
                }
            } catch (err) {
                console.error(err);
                Swal.fire({
                    title: '❌ Network Error!',
                    text: 'There was a network or server error while deleting.',
                    icon: 'error',
                    confirmButtonColor: '#ff5c00',
                });
            }
        }
    };

    const groupClientsByName = () => {
        const map = {};

        clients.forEach((client) => {
            const name = client.name || 'Unnamed';
            const data = computeData(client);

            if (!map[name]) {
                map[name] = {
                    name,
                    count: 0,
                    totalPaymentStatus: 0,
                    totalDeposit: 0,
                    totalWithdraw: 0,
                    totalBalance: 0,
                    rawIdSettled: 0,
                    cmsnSettled: 0,
                    idSettled: 0,
                    netSettlement: 0,
                };
            }

            map[name].count += 1;
            map[name].totalPaymentStatus += data.totalPaymentStatus;
            map[name].totalDeposit += data.totalDeposit;
            map[name].totalWithdraw += data.totalWithdraw;
            map[name].totalBalance += data.totalBalance;
            map[name].rawIdSettled += data.rawIdSettled;
            map[name].cmsnSettled += Math.abs(data.cmsnSettled);
            map[name].idSettled += data.idSettled;
            map[name].netSettlement += data.netSettlement;
        });

        return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    };

    const groupBrokersByName = () => {
        const map = {};

        brokers.forEach((broker) => {
            const name = broker.name || 'Unnamed';
            const data = computeData(broker);

            if (!map[name]) {
                map[name] = {
                    name,
                    count: 0,
                    totalPaymentStatus: 0,
                    totalDeposit: 0,
                    totalWithdraw: 0,
                    totalBalance: 0,
                    rawIdSettled: 0,
                    cmsnSettled: 0,
                    idSettled: 0,
                    netSettlement: 0,
                };
            }

            map[name].count += 1;
            map[name].totalPaymentStatus += data.totalPaymentStatus;
            map[name].totalDeposit += data.totalDeposit;
            map[name].totalWithdraw += data.totalWithdraw;
            map[name].totalBalance += data.totalBalance;
            map[name].rawIdSettled += data.rawIdSettled;
            map[name].cmsnSettled += Math.abs(data.cmsnSettled);
            map[name].idSettled += data.idSettled;
            map[name].netSettlement += data.netSettlement;
        });

        return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    };

    return (
        <div className="bg-gray-50 p-4 space-y-8">
            {/* Overall Summary Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-start mb-6">
                    {/* Left: Financial Dashboard Heading */}
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                        Financial Dashboard
                    </h1>

                    {/* Right: Net Book Summary Card */}
                    <div className="bg-orange-50 rounded-lg p-2 md:p-4 border border-orange-200">
                        <div className="space-y-2">
                            <span className="text-gray-800 font-bold">Net Book : </span>
                            <span className="font-bold text-purple-600">
                                {formatCurrency(combinedTotals.netSettlement)}
                            </span>
                        </div>
                    </div>
                </div>



                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h2 className="text-xl font-semibold text-blue-700 mb-4 flex items-center">
                            <Briefcase className="h-6 w-6 mr-2" />
                            Broker Totals ({brokers.length})
                        </h2>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Deposits:</span>
                                <span className="font-semibold text-green-600">{formatCurrency(brokerTotals.totalDeposit)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Withdrawals:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(brokerTotals.totalWithdraw)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Balance Withdrawals:</span>
                                <span className="font-semibold text-blue-600">{formatCurrency(brokerTotals.totalBalance)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Commission:</span>
                                <span className="font-semibold text-yellow-600">{formatCurrency(brokerTotals.totalCommission)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2 mt-2">
                                <span className="text-gray-800 font-bold">Net Settlement:</span>
                                <span className="font-bold text-purple-600">{formatCurrency(brokerTotals.netSettlement)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <h2 className="text-xl font-semibold text-green-700 mb-4 flex items-center">
                            <Users className="h-6 w-6 mr-2" />
                            Client Totals ({clients.length})
                        </h2>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Deposits:</span>
                                <span className="font-semibold text-green-600">{formatCurrency(clientTotals.totalDeposit)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Withdrawals:</span>
                                <span className="font-semibold text-red-600">{formatCurrency(clientTotals.totalWithdraw)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Balance Withdrawals:</span>
                                <span className="font-semibold text-blue-600">{formatCurrency(clientTotals.totalBalance)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total Commission:</span>
                                <span className="font-semibold text-yellow-600">{formatCurrency(clientTotals.totalCommission)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2 mt-2">
                                <span className="text-gray-800 font-bold">Net Settlement:</span>
                                <span className="font-bold text-purple-600">{formatCurrency(clientTotals.netSettlement)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                    {[
                        {
                            title: 'Total Deposits',
                            value: formatCurrency(combinedTotals.totalDeposit),
                            icon: <span className="h-6 w-6 text-green-500 text-xl">₹</span>,
                            color: 'bg-green-50 text-green-700 border-green-100'
                        },
                        {
                            title: 'Total Withdrawals',
                            value: formatCurrency(combinedTotals.totalWithdraw),
                            icon: <span className="h-6 w-6 text-red-500 text-xl">₹</span>,
                            color: 'bg-red-50 text-red-700 border-red-100'
                        },
                        {
                            title: 'Balance Withdrawals',
                            value: formatCurrency(combinedTotals.totalBalance),
                            icon: <span className="h-6 w-6 text-blue-500 text-xl">₹</span>,
                            color: 'bg-blue-50 text-blue-700 border-blue-100'
                        },
                        {
                            title: 'Total Commission',
                            value: formatCurrency(combinedTotals.totalCommission),
                            icon: <span className="h-6 w-6 text-yellow-500 text-xl">₹</span>,
                            color: 'bg-yellow-50 text-yellow-700 border-yellow-100'
                        },
                        {
                            title: 'Net Settlement',
                            value: formatCurrency(combinedTotals.netSettlement),
                            icon: <span className="h-6 w-6 text-purple-500 text-xl">₹</span>,
                            color: 'bg-purple-50 text-purple-700 border-purple-100'
                        }
                    ].map((stat, i) => (
                        <div key={i} className={`flex items-center justify-between p-4 rounded-lg border ${stat.color}`}>
                            <div>
                                <h3 className="text-sm font-medium opacity-80">{stat.title}</h3>
                                <p className="text-lg font-bold mt-1">{stat.value}</p>
                            </div>
                            <div className="p-2 rounded-full bg-white/70 shadow-sm">
                                {stat.icon}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Top Global Search Field */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="relative flex flex-col md:flex-row items-center gap-4 max-w-full mx-auto">
                    <div className="relative w-full md:flex-1">
                        <input
                            type="text"
                            placeholder="Search Brokers and Clients..."
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            value={brokerSearchTerm}
                            onChange={(e) => {
                                setBrokerSearchTerm(e.target.value);
                                setClientSearchTerm(e.target.value);
                            }}
                        />
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    </div>

                    <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        <button
                            onClick={updateAll}
                            className="text-sm bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 rounded shadow-md transition w-full md:w-auto"
                        >
                            Update All (Zero Net Book)
                        </button>

                        <button
                            onClick={deleteAll}
                            className="text-sm bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded shadow-md transition w-full md:w-auto"
                        >
                            Delete All IDS
                        </button>
                    </div>
                </div>
            </div>


            {/* Broker Table Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                    <div className="flex items-center mb-4 md:mb-0">
                        <Briefcase className="h-8 w-8 text-blue-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800">
                            Broker Summary <span className="text-blue-500">({sortedBrokers.length})</span>
                        </h2>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search brokers..."
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={brokerSearchTerm}
                                onChange={(e) => setBrokerSearchTerm(e.target.value)}
                            />
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>

                        <button
                            onClick={exportBrokersToCSV}
                            className="flex items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-600 px-4 py-2 rounded-lg border border-blue-200 transition-colors"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                        </button>
                    </div>
                </div>

                {/* Broker table */}
                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                        onClick={() => column.sortable && requestBrokerSort(column.key)}
                                    >
                                        <div className="flex items-center gap-1">
                                            {column.label}
                                            {brokerSortConfig.key === column.key && (
                                                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {groupByReferrer(sortedBrokers).map(([referrerName, brokerList]) => (
                                <React.Fragment key={referrerName}>
                                    {/* <tr className="bg-gray-100">
      <td colSpan={columns.length} className="text-sm font-bold px-3 py-2 text-orange-600 uppercase">
        Referred By: {referrerName}
      </td>
    </tr> */}
                                    {(() => {
                                        const totals = brokerList.reduce(
                                            (acc, broker) => {
                                                const data = computeData(broker);
                                                acc.totalPaymentStatus += data.totalPaymentStatus;
                                                acc.totalDeposit += data.totalDeposit;
                                                acc.totalWithdraw += data.totalWithdraw;
                                                acc.totalBalance += data.totalBalance;
                                                acc.rawIdSettled += data.rawIdSettled;
                                                acc.cmsnSettled += Math.abs(data.cmsnSettled);
                                                acc.idSettled += data.idSettled;
                                                acc.netSettlement += data.netSettlement;
                                                return acc;
                                            },
                                            {
                                                totalPaymentStatus: 0,
                                                totalDeposit: 0,
                                                totalWithdraw: 0,
                                                totalBalance: 0,
                                                rawIdSettled: 0,
                                                cmsnSettled: 0,
                                                idSettled: 0,
                                                netSettlement: 0,
                                            }
                                        );

                                        return (
                                            <tr className="bg-blue-50 text-blue-900 font-semibold">
                                                <td className="px-3 py-2 text-sm" colSpan={4}>Total for {referrerName}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalPaymentStatus)}</td>
                                                <td className="px-3 py-2 text-sm">-</td>
                                                <td className="px-3 py-2 text-sm">-</td>
                                                <td className="px-3 py-2 text-sm">-</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalDeposit)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalWithdraw)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalBalance)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.rawIdSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.cmsnSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.idSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.netSettlement)}</td>
                                                <td className="px-3 py-2 text-sm"></td>                                                <td className="px-3 py-2 text-sm"></td>
                                                <td className="px-3 py-2 text-sm"></td>
                                            </tr>
                                        );
                                    })()}

                                    {brokerList.map((broker, idx) => {
                                        const data = computeData(broker);
                                        return (
                                            <tr
                                                key={broker.userId}
                                                className={`${hoveredBrokerRow === broker.userId ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}
                                                onMouseEnter={() => setHoveredBrokerRow(broker.userId)}
                                                onMouseLeave={() => setHoveredBrokerRow(null)}
                                            >
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{idx + 1}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{referrerName}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{data.name}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{data.userId}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.totalPaymentStatus)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.idPercentage}%</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.commission}%</td>
 <td className="px-3 py-4 text-sm text-green-600 font-medium">{formatCurrency(data.totalDepositToId)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-green-600 font-medium">{formatCurrency(data.totalDeposit)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-red-600 font-medium">{formatCurrency(data.totalWithdraw)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-blue-600 font-medium">{formatCurrency(data.totalBalance)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.rawIdSettled)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-yellow-600">{formatCurrency(Math.abs(data.cmsnSettled))}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.idSettled)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-purple-600">{formatCurrency(data.netSettlement)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                    <button
                                                        onClick={() => handleEdit(broker, 'broker')}
                                                        className="text-blue-500 flex items-center hover:underline"
                                                    >
                                                        <Pencil className="h-4 w-4 mr-1" /> Edit
                                                    </button>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                    <button
                                                        onClick={() => handleDelete(broker, 'broker')}
                                                        className="text-red-500 flex items-center hover:underline"
                                                    >
                                                        <Trash className="h-4 w-4 mr-1" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}

                        </tbody>
                    </table>
                </div>
            </div>

            {/* Client Table Section */}
            <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                    <div className="flex items-center mb-4 md:mb-0">
                        <Users className="h-8 w-8 text-green-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800">
                            Client Summary <span className="text-green-500">({sortedClients.length})</span>
                        </h2>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search clients..."
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                value={clientSearchTerm}
                                onChange={(e) => setClientSearchTerm(e.target.value)}
                            />
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>

                        <button
                            onClick={exportClientsToCSV}
                            className="flex items-center justify-center bg-green-50 hover:bg-green-100 text-green-600 px-4 py-2 rounded-lg border border-green-200 transition-colors"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {clientColumns.map((column) => (
                                    <th
                                        key={column.key}
                                        scope="col"
                                        className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}`}
                                        onClick={() => column.sortable && requestClientSort(column.key)}
                                    >
                                        <div className="flex items-center gap-1">
                                            {column.label}
                                            {clientSortConfig.key === column.key && (
                                                <ArrowUpDown className="h-4 w-4 text-gray-500" />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {groupByReferrer(sortedClients).map(([referrerName, clientList]) => (
                                <React.Fragment key={referrerName}>
                                    {/* <tr className="bg-gray-100">
                                            <td colSpan={columns.length} className="text-sm font-bold px-3 py-2 text-green-600 uppercase">
                                                Referred By: {referrerName}
                                            </td>
                                        </tr> */}
                                    {(() => {
                                        const totals = clientList.reduce(
                                            (acc, client) => {
                                                const data = computeData(client);
                                                acc.totalPaymentStatus += data.totalPaymentStatus;
                                                acc.totalDeposit += data.totalDeposit;
                                                acc.totalWithdraw += data.totalWithdraw;
                                                acc.totalBalance += data.totalBalance;
                                                acc.rawIdSettled += data.rawIdSettled;
                                                acc.cmsnSettled += Math.abs(data.cmsnSettled);
                                                acc.idSettled += data.idSettled;
                                                acc.netSettlement += data.netSettlement;
                                                return acc;
                                            },
                                            {
                                                totalPaymentStatus: 0,
                                                totalDeposit: 0,
                                                totalWithdraw: 0,
                                                totalBalance: 0,
                                                rawIdSettled: 0,
                                                cmsnSettled: 0,
                                                idSettled: 0,
                                                netSettlement: 0,
                                            }
                                        );

                                        return (
                                            <tr className="bg-green-50 text-green-900 font-semibold">
                                                <td className="px-3 py-2 text-sm" colSpan={4}>Total for {referrerName}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalPaymentStatus)}</td>
                                                <td className="px-3 py-2 text-sm">-</td>
                                                <td className="px-3 py-2 text-sm">-</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalDeposit)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalWithdraw)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.totalBalance)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.rawIdSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.cmsnSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.idSettled)}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(totals.netSettlement)}</td>
                                                <td className="px-3 py-2 text-sm"></td>
                                                <td className="px-3 py-2 text-sm"></td>
                                            </tr>
                                        );
                                    })()}

                                    {clientList.map((client, idx) => {
                                        const data = computeData(client);
                                        return (
                                            <tr
                                                key={client.userId}
                                                className={`${hoveredClientRow === client.userId ? 'bg-green-50' : 'hover:bg-gray-50'} transition-colors`}
                                                onMouseEnter={() => setHoveredClientRow(client.userId)}
                                                onMouseLeave={() => setHoveredClientRow(null)}
                                            >
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{idx + 1}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{referrerName}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{data.name}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">{data.userId}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.totalPaymentStatus)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.idPercentage}%</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{data.commission}%</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-green-600 font-medium">{formatCurrency(data.totalDeposit)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-red-600 font-medium">{formatCurrency(data.totalWithdraw)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-blue-600 font-medium">{formatCurrency(data.totalBalance)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.rawIdSettled)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-yellow-600">{formatCurrency(Math.abs(data.cmsnSettled))}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(data.idSettled)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-purple-600">{formatCurrency(data.netSettlement)}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                    <button
                                                        onClick={() => handleEdit(client, 'client')}
                                                        className="text-blue-500 flex items-center hover:underline"
                                                    >
                                                        <Pencil className="h-4 w-4 mr-1" /> Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(client, 'client')}
                                                        className="text-red-500 flex items-center hover:underline"
                                                    >
                                                        <Trash className="h-4 w-4 mr-1" /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                </React.Fragment>
                            ))} <EditableRow />

                        </tbody>
                    </table>
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mt-8">
                {/* Broker Summary by Name */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <h3 className="text-xl font-bold text-blue-800 mb-4">Broker Summary by Name</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2 text-left">Name</th>
                                    <th className="px-4 py-2 text-left"># Brokers</th>
                                    <th className="px-4 py-2 text-left">Deposit</th>
                                    <th className="px-4 py-2 text-left">Withdraw</th>
                                    <th className="px-4 py-2 text-left">Balance</th>
                                    <th className="px-4 py-2 text-left">Commission</th>
                                    <th className="px-4 py-2 text-left">Net Settlement</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {groupBrokersByName().map((item) => (
                                    <tr key={item.name} className="hover:bg-blue-50">
                                        <td className="px-4 py-2 font-medium">{item.name}</td>
                                        <td className="px-4 py-2">{item.count}</td>
                                        <td className="px-4 py-2 text-green-600">{formatCurrency(item.totalDeposit)}</td>
                                        <td className="px-4 py-2 text-red-600">{formatCurrency(item.totalWithdraw)}</td>
                                        <td className="px-4 py-2 text-blue-600">{formatCurrency(item.totalBalance)}</td>
                                        <td className="px-4 py-2 text-yellow-600">{formatCurrency(item.cmsnSettled)}</td>
                                        <td className="px-4 py-2 text-purple-600">{formatCurrency(item.netSettlement)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Client Summary by Name */}
                <div className="bg-white rounded-lg shadow-lg p-4">
                    <h3 className="text-xl font-bold text-green-800 mb-4">Client Summary by Name</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2 text-left">Name</th>
                                    <th className="px-4 py-2 text-left"># Clients</th>
                                    <th className="px-4 py-2 text-left">Deposit</th>
                                    <th className="px-4 py-2 text-left">Withdraw</th>
                                    <th className="px-4 py-2 text-left">Balance</th>
                                    <th className="px-4 py-2 text-left">Commission</th>
                                    <th className="px-4 py-2 text-left">Net Settlement</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {groupClientsByName().map((item) => (
                                    <tr key={item.name} className="hover:bg-green-50">
                                        <td className="px-4 py-2 font-medium">{item.name}</td>
                                        <td className="px-4 py-2">{item.count}</td>
                                        <td className="px-4 py-2 text-green-600">{formatCurrency(item.totalDeposit)}</td>
                                        <td className="px-4 py-2 text-red-600">{formatCurrency(item.totalWithdraw)}</td>
                                        <td className="px-4 py-2 text-blue-600">{formatCurrency(item.totalBalance)}</td>
                                        <td className="px-4 py-2 text-yellow-600">{formatCurrency(item.cmsnSettled)}</td>
                                        <td className="px-4 py-2 text-purple-600">{formatCurrency(item.netSettlement)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default CombinedDashboard;
